import React, {useState, useEffect, useCallback, useMemo} from 'react';
import Avatar from '@cloudscape-design/chat-components/avatar';
import {Box, Button, SpaceBetween, Textarea} from '@cloudscape-design/components';
import {useChat} from '@ai-sdk/react';
import {isToolUIPart, getToolName} from 'ai';
import type {ToolUIPart} from 'ai';
import {useStickToBottom} from 'use-stick-to-bottom';
import ChatMessage from './chat-message';
import SuggestedMessages from './suggested-messages';
import {CassandraChatTransport, createChat, getMessages, setCognitoIdTokenProvider, setUserId} from './cassandra-api';
import {useAuth, ServiceResource} from '../hooks/use-auth';
import {fetchAuthSession} from 'aws-amplify/auth';
import {useCassandraSidePane} from './side-pane-context';
import {useRouter} from '../hooks/use-router';
import './cassandra.css';

export default function CassandraSidePane() {
    const {close, isOpen, activeChatId, setActiveChatId} = useCassandraSidePane();
    const router = useRouter();
    const visible = isOpen && router.pathname !== '/cassandra';
    const auth = useAuth();
    const credentials = auth.authInformation?.getEndpoints()?.NA?.[ServiceResource.ExplainabilityView]?.credentials;
    const userAlias = auth.authInformation?.email?.split('@')[0];

    useEffect(() => {
        if (auth.authInformation?.email) {
            setUserId(auth.authInformation.email);
            setCognitoIdTokenProvider(async () => {
                const session = await fetchAuthSession();
                return session.tokens?.idToken?.toString() ?? '';
            });
        }
    }, [auth.authInformation?.email]);

    const [pendingText, setPendingText] = useState<string | null>(null);
    const [input, setInput] = useState('');
    const {scrollRef, contentRef, isAtBottom, scrollToBottom} = useStickToBottom();

    const transport = useMemo(() => (credentials ? new CassandraChatTransport(credentials) : null), [credentials]);

    const {messages, sendMessage, status, setMessages, stop, addToolResult} = useChat({
        id: activeChatId ?? undefined,
        transport: transport ?? undefined,
        sendAutomaticallyWhen: ({messages: msgs}) => {
            const last = msgs.at(-1);
            if (!last || last.role !== 'assistant') return false;
            const questionParts = last.parts.filter((p) => isToolUIPart(p) && getToolName(p as ToolUIPart) === 'askQuestion');
            return questionParts.length > 0 && questionParts.every((p) => isToolUIPart(p) && p.state === 'output-available');
        },
    });

    useEffect(() => {
        if (activeChatId && pendingText && transport) {
            transport.setChatId(activeChatId);
            sendMessage({text: pendingText});
            setPendingText(null);
        }
    }, [activeChatId, pendingText, transport, sendMessage]);

    // Load messages when switching to an existing chat (e.g. coming back from full-page view)
    useEffect(() => {
        if (!activeChatId || !credentials) {
            setMessages([]);
            return;
        }
        if (sending || pendingText) return;
        getMessages(activeChatId, credentials).then((msgs) => setMessages(msgs));
    }, [activeChatId, credentials]);

    const sending = status === 'streaming' || status === 'submitted';

    const handleSend = useCallback(
        async (overrideText?: string) => {
            const text = (overrideText ?? input).trim();
            if (!text || sending || !credentials || !transport) return;

            try {
                if (!activeChatId) {
                    const chat = await createChat(text.slice(0, 50), credentials);
                    setActiveChatId(chat.id);
                    setInput('');
                    setPendingText(text);
                } else {
                    transport.setChatId(activeChatId);
                    setInput('');
                    sendMessage({text});
                }
            } catch (e) {
                console.error('Failed to send message', e);
            }
        },
        [input, sending, activeChatId, credentials, sendMessage, transport, setActiveChatId]
    );

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey && (e.target as HTMLElement).tagName === 'TEXTAREA') {
            e.preventDefault();
            handleSend();
        }
    };

    const handleNewChat = useCallback(() => {
        setActiveChatId(null);
        setMessages([]);
        setInput('');
    }, [setMessages, setActiveChatId]);

    return (
        <div className="cassandra-side-pane" style={{display: visible ? undefined : 'none'}}>
            <div className="cassandra-side-pane-header">
                <Box variant="h3" fontSize="heading-s" fontWeight="bold">
                    Cassandra
                </Box>
                <div className="cassandra-side-pane-header-actions">
                    <div className="cassandra-tooltip" data-tooltip="New conversation">
                        <Button variant="icon" iconName="add-plus" ariaLabel="New conversation" onClick={handleNewChat} />
                    </div>
                    <div className="cassandra-tooltip" data-tooltip="Open full screen">
                        <Button
                            variant="icon"
                            iconName="expand"
                            ariaLabel="Open full screen"
                            onClick={() => router.push('/cassandra')}
                        />
                    </div>
                    <div className="cassandra-tooltip" data-tooltip="Close chat">
                        <Button variant="icon" iconName="close" ariaLabel="Close chat" onClick={close} />
                    </div>
                </div>
            </div>
            <div className="cassandra-side-pane-messages" ref={scrollRef} role="log">
                {messages.length === 0 && !sending && (
                    <div className="cassandra-empty-state">
                        <Avatar color="gen-ai" iconName="gen-ai" ariaLabel="Cassandra" width={48} />
                        <Box variant="h3">How can I help you?</Box>
                        <Box color="text-body-secondary" textAlign="center" fontSize="body-s">
                            Ask about demand forecasts, S&OP data, or site volumes.
                        </Box>
                    </div>
                )}
                <div ref={contentRef} style={{padding: '0 12px'}}>
                    <SpaceBetween size="xs">
                        {messages.map((msg, i) => (
                            <ChatMessage
                                key={msg.id}
                                message={msg}
                                userAlias={userAlias}
                                isStreaming={sending && msg.role === 'assistant' && i === messages.length - 1}
                                onQuestionAnswer={({toolCallId, answer}) =>
                                    addToolResult({tool: 'askQuestion', toolCallId, output: answer})
                                }
                            />
                        ))}
                    </SpaceBetween>
                    {(status === 'ready' || status === 'error') && (
                        <SuggestedMessages messages={messages} onSelect={(text) => handleSend(text)} />
                    )}
                </div>
                {!isAtBottom && (
                    <div style={{position: 'sticky', bottom: 8, float: 'right', marginRight: 8}}>
                        <Button
                            variant="icon"
                            iconName="angle-down"
                            onClick={() => scrollToBottom()}
                            ariaLabel="Scroll to bottom"
                        />
                    </div>
                )}
            </div>
            <div className="cassandra-side-pane-input" onKeyDown={handleKeyDown}>
                <div className="cassandra-prompt-container">
                    <Textarea
                        value={input}
                        onChange={({detail}) => setInput(detail.value)}
                        placeholder="Type your message..."
                        disabled={sending}
                        rows={2}
                    />
                    <div className="cassandra-prompt-actions">
                        {sending ? (
                            <Button variant="icon" iconName="close" ariaLabel="Stop" onClick={() => stop?.()} />
                        ) : (
                            <Button
                                variant="primary"
                                iconName="send"
                                ariaLabel="Send message"
                                onClick={() => handleSend()}
                                disabled={!input.trim()}
                            />
                        )}
                    </div>
                </div>
                <div className="cassandra-disclaimer">Cassandra can make mistakes. Check important info.</div>
            </div>
        </div>
    );
}
